import React, { useEffect, useState } from 'react';
import { Alert, Card, CardContent, Typography, Box, CircularProgress, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { dashboardService, PLData } from '../services/dashboardService';
import { PaymentReceived } from '../types/accounting';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { accountingService } from '../services/accountingService';

// Define a unified transaction type for the dashboard display
interface DisplayTransaction {
  id: number;
  date: string;
  description: string;
  amount: string;
  type: 'Revenue' | 'Expense';
}

const StatCard: React.FC<{ title: string; value: number; color: string }> = ({ title, value, color }) => (
  <Card>
    <CardContent>
      <Typography color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h5" component="div" sx={{ color }}>
        ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </Typography>
    </CardContent>
  </Card>
);

const DashboardPage: React.FC = () => {
  const [plData, setPlData] = useState<PLData | null>(null);
  const [transactions, setTransactions] = useState<DisplayTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for date filters
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const fetchData = async (start?: string, end?: string) => {
    try {
      setLoading(true);
      setError(null);
      const [plResult, paymentsResult, expensesResult] = await Promise.all([
        dashboardService.getCompanyPL({ start_date: start, end_date: end }),
        accountingService.getPayments({ project: '', start_date: start, end_date: end }),
        accountingService.getExpenses({ project: '', submitted_by: '', start_date: start, end_date: end }),
      ]);
      setPlData(plResult);

      // Combine and normalize transactions
      const normalizedPayments: DisplayTransaction[] = paymentsResult.map((p: PaymentReceived) => ({
        id: p.id,
        date: p.date_received,
        description: p.description || `Payment from ${p.project_details?.name || 'N/A'}`,
        amount: p.amount,
        type: 'Revenue',
      }));

      const normalizedExpenses: DisplayTransaction[] = expensesResult.map(e => ({
        id: e.id,
        date: e.date,
        description: e.description || e.expense_type,
        amount: e.amount,
        type: 'Expense',
      }));

      const combined = [...normalizedPayments, ...normalizedExpenses];
      combined.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setTransactions(combined);

    } catch (err: any) {
      setError('Failed to fetch dashboard data. Please ensure the backend server is running and accessible.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFilter(); // Initial fetch with default date range
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilter = () => {
    const start = startDate ? format(startDate, 'yyyy-MM-dd') : undefined;
    const end = endDate ? format(endDate, 'yyyy-MM-dd') : undefined;
    fetchData(start, end);
  };

  const formatChartData = () => {
    if (!plData) return [];

    const combinedData: { [key: string]: { month: string; revenue?: number; expenses?: number; pnl?: number } } = {};

    plData.timeline.revenue.forEach((item: { month: string; total: number }) => {
      const month = item.month; // YYYY-MM
      if (!combinedData[month]) combinedData[month] = { month };
      combinedData[month].revenue = item.total;
    });

    plData.timeline.expenses.forEach((item: { month:string; total: number }) => {
      const month = item.month; // YYYY-MM
      if (!combinedData[month]) combinedData[month] = { month };
      combinedData[month].expenses = item.total;
    });

    // Calculate P&L
    Object.keys(combinedData).forEach(month => {
        const revenue = combinedData[month].revenue || 0;
        const expenses = combinedData[month].expenses || 0;
        combinedData[month].pnl = revenue - expenses;
    });
    
    // Sort by the YYYY-MM string, which is reliable
    const sortedData = Object.values(combinedData).sort((a, b) => a.month.localeCompare(b.month));
    
    // Format for display after sorting
    return sortedData.map(item => ({
      ...item,
      month: new Date(item.month + '-02').toLocaleString('default', { month: 'short', year: '2-digit' }),
    }));
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!plData) {
    return <Typography>No data available.</Typography>;
  }

  const chartData = formatChartData();

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Company Dashboard</Typography>
      
      {/* Date Filters */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4, flexWrap: 'wrap' }}>
        <DatePicker label="Start Date" value={startDate} onChange={setStartDate} />
        <DatePicker label="End Date" value={endDate} onChange={setEndDate} />
        <Button variant="contained" onClick={handleFilter} disabled={loading}>
          {loading ? 'Filtering...' : 'Filter'}
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box 
        sx={{ 
          display: 'flex', 
          gap: 3, 
          mb: 4,
          flexDirection: { xs: 'column', sm: 'row' } 
        }}
      >
        <Box sx={{ flex: 1 }}>
          <StatCard title="Total Revenue" value={plData.total_revenue} color="success.main" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <StatCard title="Total Expenses" value={plData.total_expenses} color="error.main" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <StatCard 
            title="Net Profit"
            value={plData.total_revenue - plData.total_expenses}
            color={(plData.total_revenue - plData.total_expenses) >= 0 ? 'primary.main' : 'error.main'}
          />
        </Box>
      </Box>

      {/* Value vs Time Graph */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Performance Over Time</Typography>
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
                <Legend />
                <Bar dataKey="revenue" fill="#4caf50" />
                <Bar dataKey="expenses" fill="#f44336" />
                <Bar dataKey="pnl" name="P&L" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Transactions</Typography>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((row) => (
                  <TableRow key={`${row.type}-${row.id}`}>
                    <TableCell>{row.date && !isNaN(new Date(row.date).getTime()) ? format(new Date(row.date), 'yyyy-MM-dd') : 'N/A'}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell><Typography color={row.type === 'Revenue' ? 'success.main' : 'error.main'}>{row.type}</Typography></TableCell>
                    <TableCell align="right">${parseFloat(row.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardPage;
